import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'

import type { IReportData, ISocialMediaFound } from '@/hooks/useReportData'

import { MetricsCard } from './MetricsCard'
import { SocialMediaList } from './SocialMediaList'
import { StatValue } from './StatValue'
import { SubjectHeader } from './SubjectHeader'

interface SubjectPanelProps {
  reportData: IReportData
  inputProfiles: ISocialMediaFound[]
}

export function SubjectPanel({ reportData, inputProfiles }: SubjectPanelProps) {
  const { t } = useTranslation()
  const hasAnyProfile =
    reportData.socialMedia.length > 0 || inputProfiles.length > 0

  const { contactInfo } = reportData
  const hasContactInfo =
    contactInfo.emails.length > 0 ||
    contactInfo.phones.length > 0 ||
    contactInfo.addresses.length > 0

  return (
    <div className="space-y-4">
      <div className="rounded-2xl backdrop-blur-md p-3 space-y-5 bg-baseColorHighlight">
        <SubjectHeader
          subject={reportData.subject}
          summary={reportData.behavioralSummary}
        />

        <AnimatePresence>
          {hasContactInfo && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.4 }}
              className="overflow-hidden"
            >
              {(() => {
                let rowIndex = 0
                return (
                  <>
                    {contactInfo.emails.map((e, i) => (
                      <ContactRow
                        key={`email-${i}`}
                        value={e.email}
                        label={
                          e.type === 'professional'
                            ? t(
                                'dashboard.newSearch.generatingReport.subject.workEmail',
                              )
                            : e.type === 'personal'
                              ? t(
                                  'dashboard.newSearch.generatingReport.subject.personalEmail',
                                )
                              : t(
                                  'dashboard.newSearch.generatingReport.subject.email',
                                )
                        }
                        showBorder={rowIndex++ > 0}
                      />
                    ))}
                    {contactInfo.phones.map((p, i) => (
                      <ContactRow
                        key={`phone-${i}`}
                        value={p.phone}
                        label={t(
                          'dashboard.newSearch.generatingReport.subject.phoneNumber',
                        )}
                        showBorder={rowIndex++ > 0}
                      />
                    ))}
                    {contactInfo.addresses.length > 0 && (
                      <ContactRow
                        key="detected-locations"
                        value={
                          <StatValue
                            value={contactInfo.addresses.length}
                            className="tabular-nums"
                          />
                        }
                        label={t(
                          'dashboard.newSearch.generatingReport.subject.detectedLocations',
                        )}
                        showBorder={rowIndex++ > 0}
                      />
                    )}
                  </>
                )
              })()}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {hasAnyProfile && (
          <motion.div
            key="social-media-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5 }}
            className="rounded-2xl backdrop-blur-md p-3 space-y-5 bg-baseColorHighlight"
          >
            <SocialMediaList
              socialMedia={reportData.socialMedia}
              inputProfiles={inputProfiles}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {reportData.hasScraperData && (
          <MetricsCard totals={reportData.totals} stats={reportData.stats} />
        )}
      </AnimatePresence>
    </div>
  )
}

function ContactRow({
  value,
  label,
  showBorder,
}: {
  value: React.ReactNode
  label: string
  showBorder: boolean
}) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
      className={
        showBorder
          ? 'flex items-baseline justify-between gap-4 py-4 border-t border-neutralColor5'
          : 'flex items-baseline justify-between gap-4 py-4'
      }
    >
      <span className="text-sm text-neutralColor flex-shrink-0">{label}</span>
      <span className="text-sm text-neutralColor60 text-right min-w-0 truncate">
        {value}
      </span>
    </motion.div>
  )
}
